import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Sottomenu del calcolo corrispettivo: stesse voci per admin e impresa
const sottoCorrispettivo = base => ([
  { to: `${base}/corrispettivo/rinnovo`, label: 'Nuova / Rinnovo' },
  { to: `${base}/corrispettivo/revisione`, label: 'Revisione' },
  { to: `${base}/corrispettivo/aumento`, label: 'Aumento classifica' },
  { to: `${base}/corrispettivo/variazioni`, label: 'Variazioni fisse' },
]);

const navAdmin = [
  { to: '/admin/dashboard', label: 'Dashboard' },
  { to: '/admin/imprese', label: 'Dati economici' },
  { to: '/admin/imprese-soa', label: 'Certificati SOA' },
  { to: '/admin/indici-istat', label: 'Indici ISTAT' },
  { to: '/admin/tabella-inail', label: 'Tabella INAIL' },
  { to: '/admin/importazione', label: 'Importazione dati' },
  { to: '/admin/utenti', label: 'Gestione utenti' },
  { to: '/admin/documenti', label: 'Documenti' },
  { to: '/admin/pagamenti', label: 'Pagamenti' },
  { label: 'Calcolo corrispettivo', children: sottoCorrispettivo('/admin') },
];

const navImpresa = [
  { to: '/impresa/dati', label: 'I miei dati e calcolo SOA' },
  { label: 'Calcolo corrispettivo', children: sottoCorrispettivo('/impresa') },
];

export default function Layout({ children }) {
  const { utente, logout } = useAuth();
  const navigate = useNavigate();
  const nav = utente?.ruolo === 'admin' ? navAdmin : navImpresa;
  const [aperta, setAperta] = useState(false);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="min-h-screen flex bg-[#EEF4FB]">

      {/* Overlay scuro su mobile quando sidebar è aperta */}
      {aperta && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setAperta(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={[
        'fixed lg:static inset-y-0 left-0 z-30',
        'w-56 bg-[#1A3A5C] text-white flex flex-col',
        'transition-transform duration-200 ease-in-out',
        aperta ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0',
      ].join(' ')}>

        <div className="px-6 py-5 border-b border-[#2E6096] flex items-center justify-between">
          <div>
            <p className="font-bold text-lg leading-tight">C.Q.S. S.r.l.</p>
            <p className="text-xs text-blue-300 mt-0.5">Gestionale SOA</p>
          </div>
          {/* Chiudi sidebar su mobile */}
          <button
            onClick={() => setAperta(false)}
            className="lg:hidden text-blue-300 hover:text-white text-xl leading-none p-1"
            aria-label="Chiudi menu"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map(item => item.children ? (
            <div key={item.label} className="pt-2">
              <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-blue-300">{item.label}</p>
              {item.children.map(sub => (
                <NavLink
                  key={sub.to}
                  to={sub.to}
                  onClick={() => setAperta(false)}
                  className={({ isActive }) =>
                    `block ml-3 pl-3 pr-3 py-1.5 rounded-lg text-sm transition border-l border-[#2E6096] ${
                      isActive
                        ? 'bg-[#2E6096] text-white font-medium'
                        : 'text-blue-200 hover:bg-[#2E6096] hover:text-white'
                    }`
                  }
                >
                  {sub.label}
                </NavLink>
              ))}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setAperta(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? 'bg-[#2E6096] text-white'
                    : 'text-blue-200 hover:bg-[#2E6096] hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-[#2E6096]">
          <p className="text-xs text-blue-300 mb-2 truncate">{utente?.email}</p>
          <NavLink
            to="/cambia-password"
            onClick={() => setAperta(false)}
            className={({ isActive }) =>
              `block text-xs mb-2 transition ${isActive ? 'text-white font-medium' : 'text-blue-200 hover:text-white'}`
            }
          >
            Cambia password
          </NavLink>
          <button
            onClick={handleLogout}
            className="w-full text-left text-xs text-blue-200 hover:text-white transition"
          >
            Esci
          </button>
        </div>
      </aside>

      {/* Contenuto principale */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar visibile solo su mobile */}
        <header className="lg:hidden bg-[#1A3A5C] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 shadow-md">
          <button
            onClick={() => setAperta(true)}
            className="text-white p-1 text-2xl leading-none"
            aria-label="Apri menu"
          >
            ☰
          </button>
          <span className="font-semibold text-sm">C.Q.S. S.r.l.</span>
          <button
            onClick={handleLogout}
            className="text-xs text-blue-200 hover:text-white"
          >
            Esci
          </button>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
